import React, { useEffect, useState } from 'react';
import SearchLogs from 'forms/SearchLogs';
import {
  Box,
  Container,
  Modal,
  Paper,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import ReactJson from 'react-json-view';

import DataTable from 'components/DataTable';
import useTable from 'hooks/useTable';
import { getFormattedDate } from 'utils/transform';
import moment from 'moment';
import { Refresh } from '@material-ui/icons';
import api from 'services/api';
import { toast } from 'react-toastify';

const useStyles = makeStyles(theme => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContent: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2),
    outline: 'none',
    borderRadius: 10,
    margin: 'auto',
    width: '100%',
    maxWidth: 700,
    maxHeight: '80vh',
    overflow: 'auto',
  },
}));

function Logs() {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [erpStatus, setErpStatus] = useState(-1);
  const [erpStatusCreatedAt, setErpStatusCreatedAt] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [remainingTimeText, setRemainingTimeText] = useState('');
  const [isAllowed, setIsAllowed] = useState(false);

  const classes = useStyles();

  const { data, onRefresh, ...dataTableProps } = useTable('logs', {
    download: false,
    viewColumns: false,
    print: false,
    search: false,
    onRowClick,
    params: {
      sort: 'created_at',
      order: 'desc',
    },
  });

  function onRowClick(row) {
    setSelectedRow(row);
  }

  const closeModal = () => {
    setSelectedRow(null);
  };

  const columns = [
    {
      name: 'model_name',
      label: 'Modelo',
    },
    {
      name: 'name',
      label: 'Nome',
      options: {
        customBodyRender: (_, response) => {
          return response?.rowData?.[5]?.name ?? '-';
        },
      },
    },
    {
      name: 'response',
      label: 'Status',
      options: {
        customBodyRender: response => {
          return response?.error ? 'Erro' : 'Sucesso';
        },
        sort: false,
      },
    },
    {
      name: 'before',
      label: 'Tipo',
      options: {
        customBodyRender: before => {
          return before?.id || before?.cpf ? 'Edição' : 'Criação';
        },
        sort: false,
      },
    },
    {
      name: 'before',
      label: 'Antes',
      options: {
        customBodyRender: before => {
          return before?.id || before?.cpf
            ? JSON.stringify(before).substring(0, 20) + '}...'
            : '{...}';
        },
        sort: false,
      },
    },
    {
      name: 'after',
      label: 'Depois',
      options: {
        customBodyRender: after => {
          return JSON.stringify(after).substring(0, 50) + '}...';
        },
        sort: false,
      },
    },
    {
      name: 'created_at',
      label: 'Data Criação',
      options: {
        customBodyRender: created_at => {
          return getFormattedDate(created_at, 'dd/MM/yyyy');
        },
      },
    },
  ];

  const handleFilter = e => {
    if (e.value) {
      const filterAux = {
        [e.field.value]: e?.value,
        filterByColumn: e?.filterByColumn?.value,
      };

      onRefresh(filterAux, true);
      return;
    }

    onRefresh();
  };

  const getErpStatus = async () => {
    try {
      if (!calculateElapsedTime()) {
        return;
      }
      setLoadingStatus(true);
      const { data } = await api.get('/eme4-logs/check-status');
      setErpStatus(data?.status_eme4);
      setErpStatusCreatedAt(data?.created_at);
    } catch (error) {
      toast.error('Ocorreu um erro ao verificar o status do ERP');
    } finally {
      setLoadingStatus(false);
    }
  };

  const calculateElapsedTime = () => {
    const now = new Date();

    const difference = now.getTime() - new Date(erpStatusCreatedAt).getTime();
    const remainingTime = 5 * 60 * 1000 - difference;
    if (difference < 5 * 60 * 1000) {
      const minutes = Math.floor(remainingTime / 60000);
      const seconds = Math.floor((remainingTime % 60000) / 1000);
      setRemainingTimeText(
        `Faltam ${minutes} minutos e ${seconds} segundos para carregar o status novamente.`,
      );
      setIsAllowed(false);
      return false;
    }

    setRemainingTimeText('Verificar status');
    setIsAllowed(true);
    return true;
  };

  useEffect(() => {
    getErpStatus();
  }, []);

  useEffect(() => {
    if (!data?.length || dataLoaded) return;

    onRefresh();

    setDataLoaded(true);
  }, [data]);

  return (
    <Container>
      <Modal
        className={classes.modal}
        open={!!selectedRow}
        onClose={closeModal}
      >
        <div className={classes.modalContent}>
          <h3>Antes</h3>
          <Box border="1px solid gray" borderRadius={8} p={2}>
            {selectedRow?.before ? (
              <ReactJson src={selectedRow?.before} />
            ) : (
              <h4>Nenhum dado informado.</h4>
            )}
          </Box>
          <h3>Depois</h3>
          <Box border="1px solid gray" borderRadius={8} p={2}>
            {selectedRow?.after ? (
              <ReactJson src={selectedRow?.after} />
            ) : (
              <h4>Nenhum dado informado.</h4>
            )}
          </Box>
        </div>
      </Modal>
      <Paper>
        <Box p={1} mb={1}>
          <Box m={1}>
            <Box
              border="1px solid #c4c4c4"
              p={2}
              borderWidth={1}
              borderRadius={4}
            >
              {loadingStatus ? (
                <div>Status ERP: Carregando...</div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    Status ERP:{' '}
                    {erpStatus !== -1 &&
                      (erpStatus === 1 ? (
                        <a
                          style={{
                            color: '#22bb33',
                            fontSize: 16,
                            fontWeight: 600,
                          }}
                        >
                          Ativo{' '}
                        </a>
                      ) : (
                        <aa
                          style={{
                            color: 'red',
                            fontSize: 16,
                            fontWeight: 600,
                          }}
                        >
                          Inativo{' '}
                        </aa>
                      ))}
                    - Ultima verificação:{' '}
                    {moment(erpStatusCreatedAt).format('DD/MM/YYYY HH:mm')}
                  </div>
                  {erpStatus === -1 ? (
                    <div
                      onClick={getErpStatus}
                      style={{
                        color: 'blue',
                        textDecorationLine: 'underline',
                        cursor: 'pointer',
                        marginLeft: 5,
                      }}
                    >
                      {' '}
                      Checar status
                    </div>
                  ) : (
                    <Tooltip
                      onMouseEnter={calculateElapsedTime}
                      onMouseLeave={calculateElapsedTime}
                      title={remainingTimeText}
                    >
                      <Refresh
                        style={{
                          fontSize: 18,
                          marginLeft: 5,
                          cursor: !isAllowed ? 'not-allowed' : 'pointer',
                        }}
                        onClick={getErpStatus}
                      />
                    </Tooltip>
                  )}
                </div>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
      <Paper>
        <Box p={1} mb={1}>
          <SearchLogs onSubmit={handleFilter} />
        </Box>
      </Paper>
      <DataTable
        title="Logs"
        columns={columns}
        data={data}
        {...dataTableProps}
      />
    </Container>
  );
}

export default Logs;
